/* ==================================================
  c_cart-novelty-block.js — 客注宅配（order_type=4）ノベルティ自動付与抑止モジュール
  - スタッフが客注:宅配（order_type=4）で購入する際、Qikify によるノベルティ自動追加を防ぐため、
    実商品の line item property に `_novelty_block="1"` を付与する。
  - 一般顧客がノベルティを受け取れるよう、一般カートへの遷移時・チェックアウト前に `_novelty_block="0"`
    で確実に解除する（Shopify Ajax API の仕様上、properties は空オブジェクトにできないため "0" で上書き）。
  - 刺繍の客注でもノベルティを付けないため、Pulse が生成する「刺繍加工代」行（_job あり・
    Location / _Job なし）を運び役にする。無料キャンペーンの ¥0 加工代も対象。
  - 刺繍本体の行（Location / _Job）、定期購入商品（selling_plan_allocation）、¥0 の通常商品、
    product_type='ノベルティ' は対象外（候補が無ければ fail-open）。
  - すでに `_novelty_block` を持つ行がある場合は、行の結合崩れを防ぐためその行を優先する
    （ただし刺繍加工代の行があれば加工代の中で選ぶ。詳細は pickTargetLine）。
  - 決して例外を throw / reject しない（never-reject 契約）。
  - Pulse と対になる行（_id / p_id）へ書き込むと line key が変わるが、Pulse はページ読み込み時に
    取得した cart をクロージャ内に保持しており、テーマから更新できない。古い key のまま数量変更
    されると本体と加工代の数量がズレる（代金が実態と合わなくなる）ため、カートページに留まる
    場合（自動解除）と bfcache で戻った場合に限り 1 回だけ再読み込みして Pulse に取り直させる。
    購入ボタン経由の解除・付与は直後にページ遷移するため再読み込みしない。
    再読み込みは cart 属性を一切消さないので ADR 0001（表示時クリア禁止）には抵触しない。
===================================================*/
(() => {
  'use strict';

  if (typeof window !== 'undefined' && window.CCartNoveltyBlock) return;

  const LOG_PREFIX = '[c_cart-novelty-block]';
  const PROPERTY_KEY = '_novelty_block';
  const BLOCK_VALUE = '1';
  const RELEASE_VALUE = '0';
  const NOVELTY_PRODUCT_TYPE = 'ノベルティ';

  function cartChangeUrl() {
    const root = (window.Shopify?.routes?.root || '/').replace(/\/$/, '');
    const base = window.themeVariables?.routes?.cartChangeUrl || `${root}/cart/change`;
    const pathname = `${base}.js`;
    // URL の絶対パス化: 一部サードパーティアプリ（例: Pulse 刺繍アプリ）が window.fetch を
    // monkey-patch し、相対プレフィックス "/cart/change"（および "/cart/update"）を検知して
    // /cart/update.js へハイジャックし properties を欠落させる事象を防ぐ。
    // "https:" から始まる絶対 URL にすることでハイジャックを回避し直接 Shopify に送信する。
    if (typeof window !== 'undefined' && window.location && window.location.origin) {
      return `${window.location.origin}${pathname.startsWith('/') ? '' : '/'}${pathname}`;
    }
    return pathname;
  }

  function cartJsonUrl() {
    const root = (window.Shopify?.routes?.root || '/').replace(/\/$/, '');
    const base = window.themeVariables?.routes?.cartUrl || `${root}/cart`;
    const pathname = `${base}.js`;
    if (typeof window !== 'undefined' && window.location && window.location.origin) {
      return `${window.location.origin}${pathname.startsWith('/') ? '' : '/'}${pathname}`;
    }
    return pathname;
  }

  /* 純関数: 該当行がブロック中（"1"）か判定。厳格比較（"1" のみ true）。 */
  function isBlocked(item) {
    return Boolean(item && item.properties && item.properties[PROPERTY_KEY] === BLOCK_VALUE);
  }

  /* 純関数: Pulse が生成する「刺繍加工代」行か判定。
     刺繍本体は Location / _Job（大文字 J）を持ち、加工代は _job のみを持つ。
     _id / _paidpers は要求しない（¥0 キャンペーン用の加工代が同じ形とは限らないため）。 */
  function isEmbroideryFeeLine(item) {
    const p = item && item.properties;
    return Boolean(p && p._job && !p.Location && !p._Job);
  }

  /* 純関数: Pulse が数量を連動させる対の行か（_id / p_id を持つ）。再読み込み判定専用。 */
  function isPairedLine(item) {
    const p = item && item.properties;
    return Boolean(p && (p._id || p.p_id));
  }

  /* 純関数: ノベルティブロックの運び役として適格か。
     ノベルティ商品は常に不可。刺繍加工代は ¥0 でも可、それ以外は有料行のみ。 */
  function isEligibleCarrier(item) {
    if (!item || item.product_type === NOVELTY_PRODUCT_TYPE) return false;
    if (isEmbroideryFeeLine(item)) return true;
    return typeof item.final_line_price === 'number' && item.final_line_price > 0;
  }

  /* 純関数: アプリ管理行または制約行（定期購入、刺繍本体）か判定。該当行は候補から完全除外。
     _job を持つ行のうち刺繍加工代だけは除外しない（刺繍の客注ではこれが唯一の運び役）。 */
  function isAppManagedOrRestricted(item) {
    if (!item) return false;
    if (item.selling_plan_allocation) return true;
    const p = item.properties;
    if (!p) return false;
    if (p.Location || p._Job) return true;
    return Boolean(p._job) && !isEmbroideryFeeLine(item);
  }

  /* 純関数: ブロック対象とする行を選択する。
     1. 刺繍加工代の行があれば必ずその中から選ぶ（刺繍の客注ではマーカーを加工代の明細に付ける）。
        すでに _novelty_block キーを持つ加工代行を優先し、なければカート順で先頭（¥0 も可）。
        加工代は Pulse の _id で行が一意になるため、他行との結合崩れは起きない。
     2. 加工代が無ければ、すでに _novelty_block キーを持つ行（"0" または "1"）があり、かつ非制約であれば優先
        （運び役の不変性: 割引等で価格変動しても同一行を維持）。
     3. なければ適格かつ非制約の通常行から先頭を選択。
     4. なければ null（fail-open）。 */
  function pickTargetLine(cart) {
    const items = (cart && cart.items) || [];
    const hasBlockKey = (i) => Boolean(i && i.properties && i.properties[PROPERTY_KEY] !== undefined);

    const feeLines = items.filter(
      (i) => isEmbroideryFeeLine(i) && isEligibleCarrier(i) && !isAppManagedOrRestricted(i)
    );
    if (feeLines.length > 0) {
      return feeLines.find(hasBlockKey) || feeLines[0];
    }

    const existingCarrier = items.find(hasBlockKey);
    if (existingCarrier && !isAppManagedOrRestricted(existingCarrier)) {
      return existingCarrier;
    }

    const candidates = items.filter(
      (i) => isEligibleCarrier(i) && !isAppManagedOrRestricted(i)
    );
    return candidates.length > 0 ? candidates[0] : null;
  }

  /* 純関数: _novelty_block="1" を付与した新しい properties を返す（元オブジェクト不変）。 */
  function withNoveltyBlock(properties) {
    return {
      ...(properties || {}),
      [PROPERTY_KEY]: BLOCK_VALUE,
    };
  }

  /* 純関数: _novelty_block="0" で解除した新しい properties を返す（決してキー削除しない）。 */
  function withoutNoveltyBlock(properties) {
    return {
      ...(properties || {}),
      [PROPERTY_KEY]: RELEASE_VALUE,
    };
  }

  /* 純関数: ブロック中の行（_novelty_block === "1"）を検索して返す。 */
  function findBlockedLine(cart) {
    const items = (cart && cart.items) || [];
    return items.find((i) => isBlocked(i)) || null;
  }

  /* DOM 補正: /cart/change.js で key が変わった場合、DOM の [data-key] および削除ボタンの href を追随させる。 */
  function patchDomLineKey(oldKey, newKey) {
    if (!oldKey || !newKey || oldKey === newKey) return;
    if (typeof document === 'undefined' || !document.querySelector || !document.querySelector('.c_main-cart')) return;

    // 1. [data-key] 属性の更新（数量セレクタ等）
    const safeKey = String(oldKey).replace(/["\\]/g, '\\$&');
    let keyEls = [];
    try {
      keyEls = document.querySelectorAll(`[data-key="${safeKey}"]`);
    } catch (_e) {
      const allEls = document.querySelectorAll('.c_main-cart [data-key], [data-key]');
      keyEls = Array.from(allEls || []).filter((el) => {
        const val = typeof el.getAttribute === 'function' ? el.getAttribute('data-key') : (el.attrs && el.attrs['data-key']);
        return val === oldKey;
      });
    }
    if (keyEls && typeof keyEls.forEach === 'function') {
      keyEls.forEach((el) => el.setAttribute('data-key', newKey));
    }

    // 2. 削除ボタンの href 補正（a.line-item__remove-button）
    const encodedOldKey = encodeURIComponent(oldKey);
    const encodedNewKey = encodeURIComponent(newKey);
    const lowerEncodedOldKey = encodedOldKey.toLowerCase();

    const patchHref = (btn) => {
      const rawHref = btn.getAttribute('href');
      if (!rawHref) return;
      let updatedHref = rawHref;
      if (updatedHref.includes(oldKey)) {
        updatedHref = updatedHref.split(oldKey).join(newKey);
      } else if (updatedHref.includes(encodedOldKey)) {
        updatedHref = updatedHref.split(encodedOldKey).join(encodedNewKey);
      } else if (updatedHref.includes(lowerEncodedOldKey)) {
        updatedHref = updatedHref.split(lowerEncodedOldKey).join(encodedNewKey);
      }
      if (updatedHref !== rawHref) {
        btn.setAttribute('href', updatedHref);
      }
    };

    // 親 .line-item が存在する場合はそのスコープ内を優先更新
    if (keyEls && keyEls.length > 0) {
      keyEls.forEach((el) => {
        const lineItem = el.closest ? el.closest('.line-item') : null;
        if (lineItem) {
          const btns = lineItem.querySelectorAll('a.line-item__remove-button');
          if (btns && typeof btns.forEach === 'function') btns.forEach(patchHref);
        }
      });
    }

    // フォールバック: 全体から更新
    const removeButtons = document.querySelectorAll('.c_main-cart a.line-item__remove-button');
    if (removeButtons && typeof removeButtons.forEach === 'function') {
      removeButtons.forEach(patchHref);
    }
  }

  /* スタッフトークン確認ヘルパー */
  function getActiveStaffToken() {
    if (typeof CStaffSession !== 'undefined' && typeof CStaffSession?.getToken === 'function') {
      return CStaffSession.getToken();
    }
    if (typeof window !== 'undefined' && typeof window.CStaffSession?.getToken === 'function') {
      return window.CStaffSession.getToken();
    }
    return '';
  }

  /* Pulse と対になる行へ書き込んだか（bfcache 復元後も JS ヒープごと保持される）。
     consumePairedLineWritten() で読むと同時に下ろす。 */
  let pairedLineWritten = false;

  function consumePairedLineWritten() {
    const written = pairedLineWritten;
    pairedLineWritten = false;
    return written;
  }

  /* Pulse のクロージャが保持する cart を取り直させるための再読み込み。
     万一 解除が効かず毎回書き込みが起きても無限ループしないよう、sessionStorage の時刻で
     RELOAD_GUARD_MS 以内の再実行を抑止する。storage が使えない環境では再読み込み自体を諦める。 */
  const RELOAD_GUARD_KEY = 'c_novelty-block-reload-at';
  const RELOAD_GUARD_MS = 10000;
  function safeReload() {
    try {
      const storage = window.sessionStorage;
      const last = Number(storage.getItem(RELOAD_GUARD_KEY) || 0);
      if (Date.now() - last < RELOAD_GUARD_MS) return;
      storage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
      window.location.reload();
    } catch (_e) {
      /* 再読み込みできなくても購入導線は妨げない */
    }
  }

  /* 数量セレクタ・削除ボタンがあるのはカートページのみ（cart_type=page）。 */
  function isCartPage() {
    return Boolean(typeof document !== 'undefined' && document.querySelector?.('.c_main-cart'));
  }

  /* 非同期: ノベルティブロックを付与（H1: in-flight guard 付き、never-reject） */
  let applyInFlight = null;
  async function applyNoveltyBlock(cart) {
    if (applyInFlight) return applyInFlight;

    applyInFlight = (async () => {
      try {
        let currentCart = cart;
        if (!currentCart) {
          const res = await fetch(cartJsonUrl(), {
            cache: 'no-store',
            headers: { Accept: 'application/json' },
          });
          if (!res.ok) throw new Error(`cart fetch failed: ${res.status}`);
          currentCart = await res.json();
        }

        if (findBlockedLine(currentCart)) {
          return; // 既にブロック中の行が存在する（冪等）
        }

        const target = pickTargetLine(currentCart);
        if (!target) {
          console.error(`${LOG_PREFIX} no eligible line to apply novelty block`);
          return;
        }

        if (target.properties && target.properties[PROPERTY_KEY] === BLOCK_VALUE) {
          return; // 既に付与済み（冪等）
        }

        const newProps = withNoveltyBlock(target.properties || {});
        const oldKeys = new Set((currentCart.items || []).map((item) => item.key));
        const changeRes = await fetch(cartChangeUrl(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          keepalive: true,
          body: JSON.stringify({
            id: target.key,
            quantity: target.quantity,
            properties: newProps,
          }),
        });

        if (!changeRes.ok) throw new Error(`apply novelty block failed: ${changeRes.status}`);
        if (isPairedLine(target)) pairedLineWritten = true;
        const updatedCart = await changeRes.json();

        // data-key DOM 補正
        const newTarget = (updatedCart.items || []).find(
          (item) =>
            !oldKeys.has(item.key) &&
            item.variant_id === target.variant_id &&
            item.properties &&
            item.properties[PROPERTY_KEY] === BLOCK_VALUE
        );
        if (newTarget && newTarget.key) {
          patchDomLineKey(target.key, newTarget.key);
        }
      } catch (error) {
        console.error(`${LOG_PREFIX} applyNoveltyBlock failed`, error);
      }
    })().finally(() => {
      applyInFlight = null;
    });

    return applyInFlight;
  }

  /* 非同期: ノベルティブロックを解除（H1: in-flight guard 付き、never-reject） */
  let clearInFlight = null;
  async function clearNoveltyBlock(cart) {
    if (clearInFlight) return clearInFlight;

    clearInFlight = (async () => {
      try {
        if (getActiveStaffToken()) return; // スタッフモード中は解除しない

        let currentCart = cart;
        if (!currentCart) {
          const res = await fetch(cartJsonUrl(), {
            cache: 'no-store',
            headers: { Accept: 'application/json' },
          });
          if (!res.ok) throw new Error(`cart fetch failed: ${res.status}`);
          currentCart = await res.json();
        }

        let clearedCount = 0;
        for (let i = 0; i < 10; i++) {
          const blockedLine = findBlockedLine(currentCart);
          if (!blockedLine) break;

          const oldKeys = new Set((currentCart.items || []).map((item) => item.key));
          const newProps = withoutNoveltyBlock(blockedLine.properties || {});
          const changeRes = await fetch(cartChangeUrl(), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            keepalive: true,
            body: JSON.stringify({
              id: blockedLine.key,
              quantity: blockedLine.quantity,
              properties: newProps,
            }),
          });

          if (!changeRes.ok) throw new Error(`clear novelty block failed: ${changeRes.status}`);
          if (isPairedLine(blockedLine)) pairedLineWritten = true;
          clearedCount++;
          const updatedCart = await changeRes.json();

          const newTarget = (updatedCart.items || []).find(
            (item) =>
              !oldKeys.has(item.key) &&
              item.variant_id === blockedLine.variant_id &&
              item.properties &&
              item.properties[PROPERTY_KEY] === RELEASE_VALUE
          );
          if (newTarget && newTarget.key) {
            patchDomLineKey(blockedLine.key, newTarget.key);
          }
          currentCart = updatedCart;
        }

        if (clearedCount > 0 && typeof document !== 'undefined') {
          if (typeof CustomEvent === 'function') {
            document.documentElement?.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          }
        }

        // ブロック行が完全に消えた場合のみ DOM フラグを解除（残っている場合は fail-closed 維持のため残す）
        if (!findBlockedLine(currentCart) && typeof document !== 'undefined') {
          const flagEl = document.querySelector?.('.c_main-cart[data-novelty-block-present="true"]');
          if (flagEl) {
            flagEl.removeAttribute('data-novelty-block-present');
          }
          const scriptFlagEl = document.querySelector?.('script[data-novelty-block-present="true"]');
          if (scriptFlagEl) {
            scriptFlagEl.removeAttribute('data-novelty-block-present');
          }
        }
      } catch (error) {
        console.error(`${LOG_PREFIX} clearNoveltyBlock failed`, error);
      }
    })().finally(() => {
      clearInFlight = null;
    });

    return clearInFlight;
  }

  function shouldAutoRun() {
    if (getActiveStaffToken()) return false;
    if (typeof document === 'undefined') return false;

    const scriptEl = document.querySelector?.('script[data-novelty-block-present="true"]');
    if (scriptEl) return true;

    const sectionEl = document.querySelector?.('.c_main-cart[data-novelty-block-present="true"]');
    if (sectionEl) return true;

    return false;
  }

  /* 自動解除。Pulse と対の行を書き換えたらカートページに限り再読み込みする。
     カート以外のページではフラグを下ろすだけ（通常の遷移で開くカートページは新規読み込みで
     Pulse も最新になる。解除前に bfcache に入ったカートページへ「戻る」場合だけは拾えない）。 */
  async function run() {
    try {
      if (!shouldAutoRun()) return;
      await clearNoveltyBlock();
      if (consumePairedLineWritten() && isCartPage()) safeReload();
    } catch (error) {
      console.error(`${LOG_PREFIX} run failed`, error);
    }
  }

  /* bfcache 復元時は Pulse の cart も離脱前のまま。書き込み済みなら run() より先に判定する
     （run() はスタッフモード中や DOM フラグ解除後は何もしないため、そこでは拾えない）。 */
  function onPageShow(event) {
    if (!event || !event.persisted) return undefined;
    if (consumePairedLineWritten()) {
      // カート以外のページではフラグを下ろすだけ（加工代の数量操作はカートページにしか無い）
      if (isCartPage()) safeReload();
      return undefined;
    }
    return run();
  }

  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => { void run(); });
    } else {
      void run();
    }
  }

  if (typeof window !== 'undefined') {
    window.addEventListener('pageshow', onPageShow);
  }

  window.CCartNoveltyBlock = {
    PROPERTY_KEY,
    BLOCK_VALUE,
    RELEASE_VALUE,
    NOVELTY_PRODUCT_TYPE,
    isBlocked,
    isEligibleCarrier,
    isAppManagedOrRestricted,
    isEmbroideryFeeLine,
    isPairedLine,
    pickTargetLine,
    withNoveltyBlock,
    withoutNoveltyBlock,
    findBlockedLine,
    applyNoveltyBlock,
    clearNoveltyBlock,
    consumePairedLineWritten,
    run,
  };
})();
